import React, { useEffect, useState } from "react";
import styles from "./learningZonePage.module.css";
import LearningZoneContent from "../../../components/learningZoneContent/LearningZoneContent";
import LearningZoneBtns from "../../../components/learningZoneBtns/LearningZoneBtns";
import LearningZoneChat from "../../../components/learningZoneChat/LearningZoneChat";
import LearningZoneToolbox from "../../../components/learningZoneToolbox/LearningZoneToolbox";
import axios from "axios";
import { useParams } from "react-router-dom";

const apiUrl = process.env.REACT_APP_URL;

const LearningZonePage = () => {
  /* Learning Zone Page contains learning tools, that are used to practice a language. Each learning tool can contain 1 or more learning units. A learning unit is a single language exercise of one of four types: vocabulary, cloze, multiple choice, or image to word. Every registered user can create a learning zone and add learning tools and learning units to it. The learning zone creator can invite other users to join it. He can also remove users from the learning zone. He is able to view the list of current members of the learning zone. Every member of the learning zone can create learning tools and units within it, and he may edit and delete tools and units created by him / her. The learning zone creator may edit and delete all tools and units in the learning zone. */

  const [username, setUsername] = useState("");
  const [lzCreatorUsername, setLzCreatorUsername] = useState("");
  const [learningZoneData, setLearningZoneData] = useState(null);
  const [isCreatorOrAdmin, setIsCreatorOrAdmin] = useState(false);
  const [maxUnitsPerTool, setMaxUnitsPerTool] = useState(5);

  const { learningZoneId } = useParams();

  /* Get all learning zone data from the database - basic data, learning tools, learning units, solutions, members, invited users, website restrictions (max units per tool). */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${apiUrl}/auth/user`, {
          withCredentials: true,
        });

        if (res.status === 200 && res.data.success) {
          setUsername(res.data.username);

          const lzRes = await axios.get(
            `${apiUrl}/learning_zones/single_lz/${learningZoneId}`,
            {
              withCredentials: true,
              params: {},
            }
          );

          if (lzRes.data) {
            setLearningZoneData(lzRes.data);

            setLzCreatorUsername(
              lzRes.data.learningZone.basicLZInfo[0].creator_username
            );

            setMaxUnitsPerTool(
              lzRes.data.learningZone.lZsystemRestrictions[0].max_units_per_tool
            );

            if (username !== "" && username === lzCreatorUsername) {
              setIsCreatorOrAdmin(true);
            } else {
              try {
                const adminRes = await axios.get(`${apiUrl}/auth/admin`, {
                  withCredentials: true,
                });

                if (
                  adminRes.status === 200 &&
                  adminRes.data.success &&
                  adminRes.data.role &&
                  adminRes.data.role === "Admin"
                ) {
                  setIsCreatorOrAdmin(true);
                }
              } catch (error) {
                console.error("Error in checking admin role:", error);
              }
            }
          } else {
            console.error("Error in obtaining the learning zone data");
          }
        }
      } catch (error) {
        console.error("Error in fetching data:", error);
      }
    };
    fetchData();
  }, [username, learningZoneId, lzCreatorUsername]);

  /**
   * Add a new learning tool to the displayed list on the page (this function is called after the new learning tool has already been added to the db).
   * @param {*} newTool - an object with the new tool data
   */
  const addNewToolToList = (newTool) => {
    setLearningZoneData((prevData) => {
      const newLearningTool = {
        learning_tool_id: newTool.learning_tool_id,
        learning_zone_id: learningZoneId,
        tool_type_id: newTool.tool_type_id,
        is_active: 1,
        units: [newTool.newUnit],
        username: username,
      };

      const newState = {
        ...prevData,
        learningZone: {
          ...prevData.learningZone,
          learningToolsWithUnits: [
            newLearningTool,
            ...prevData.learningZone.learningToolsWithUnits,
          ],
        },
      };

      return newState;
    });
  };

  /**
   * Remove a learning tool from the displayed list (this function is called after the tool has already been removed from the db)
   * @param {*} toolId - the id of the learning tool to remove
   */
  const removeToolFromList = (toolId) => {
    setLearningZoneData((prevData) => ({
      ...prevData,
      learningZone: {
        ...prevData.learningZone,
        learningToolsWithUnits:
          prevData.learningZone.learningToolsWithUnits.filter(
            (tool) => tool.learning_tool_id !== toolId
          ),
      },
    }));
  };

  if (!learningZoneData || maxUnitsPerTool === null) {
    return <div>Loading...</div>;
  }

  return (
    <div className={styles.main}>
      <section className={styles.mainLayout}>
        <div className={styles.left}>
          <LearningZoneBtns
            username={username}
            isCreatorOrAdmin={isCreatorOrAdmin}
            lzCreatorUsername={lzCreatorUsername}
            learningZoneData={learningZoneData}
          />
          <LearningZoneChat
            currUsername={username}
            learningZoneId={learningZoneId}
            isCreatorOrAdmin={isCreatorOrAdmin}
          />
        </div>
        <div className={styles.middle}>
          <section className={styles.learningZone}>
            <LearningZoneContent
              currUsername={username}
              lzCreatorUsername={lzCreatorUsername}
              isCreatorOrAdmin={isCreatorOrAdmin}
              learningZoneData={learningZoneData.learningZone}
              removeToolFromList={removeToolFromList}
              maxUnitsPerTool={maxUnitsPerTool}
            />
          </section>
        </div>
        <div className={styles.right}>
          <LearningZoneToolbox
            learningZoneId={learningZoneId}
            addNewToolToList={addNewToolToList}
          />
        </div>
      </section>
    </div>
  );
};

export default LearningZonePage;
